import { Inject, Injectable, InjectionToken, Optional, TransferState, makeStateKey } from '@angular/core';

import {
  Currency,
  DISPLAY_CURRENCY_COOKIE_NAME,
  DistanceUnit,
  LogLevel,
  TrackingData
} from '@hdms/shared/common-types';

import { CookieService } from '../cookie/cookie.service';
import { removeEmptyObjectProps } from '../helpers';
import { PlatformService } from '../platform/platform.service';
import { WindowRefService } from '../window-ref/window-ref.service';

import {
  CategoryData,
  CountryLanguage,
  CustomLinks,
  DevBuild,
  EnvWindow,
  FrontendMode,
  HostContext,
  LegacyEnv,
  MobileAppParameters,
  PhoneData,
  RapidPayConfig,
  RawEnv
} from './environment.types';

export const ENVIRONMENT = new InjectionToken<RawEnv>('core-util.environment');
export const GLOBAL_ENVIRONMENT = new InjectionToken<RawEnv>('core-util.globalEnvironment');

export function generateLanguageTag(locale: string, language?: string, country?: string): string {
  return language && language === country.toLowerCase() ? language : locale.replace('_', '-');
}

// populated via webpack-define plug-in during build
// its value is retrieved from process.env provided by CI
// eslint-disable-next-line @typescript-eslint/naming-convention
declare const __HDMS_FE_BUILD_VERSION__: string;
const BUILD_VERSION = typeof __HDMS_FE_BUILD_VERSION__ !== 'undefined' ? __HDMS_FE_BUILD_VERSION__ : '';

const STATE_KEY = makeStateKey<RawEnv>('envService.state');

export const BEARER_TOKEN_PLACEHOLDER = '{USER_BEARER_TOKEN_PLACEHOLDER}';

/**
 * update structure of window.__env.hdms to meet the expectation
 * @param env
 */
export function parseHdmsEnv(env: LegacyEnv): RawEnv {
  return env
    ? {
        ...env,
        currency: {
          code: env.currencyCode,
          pattern: env.currencyPattern,
          symbol: env.defaultCurrencySymbol
        },
        rapidPayConfig: {
          url: env.rapidPayUrl,
          merchantName: env.merchantName
        },
        salesOrgId: env.rapidPaySorg
      }
    : {};
}

/**  **********************************************************  **
 *                                                                *
 *   !!!! DO NOT ADD NEW PROPS TO THIS SERVICE ON YOUR OWN !!!!   *
 *                                                                *
 *   Any extension of properties has to be closely aligned with   *
 *   NGC team to ensure the properties can be properly migrated   *
 *                to Universal Web Application.                   *
 *                         Thank you!                             *
 *                                                                *
 **  **********************************************************  **/

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  public legacyEnv: LegacyEnv;

  private readonly win = this.winRef.nativeWindow;
  private readonly isBrowser = this.platform.isBrowser;
  private localEnv: RawEnv & LegacyEnv;

  constructor(
    @Optional() @Inject(ENVIRONMENT) staticEnv: RawEnv,
    @Optional() @Inject(GLOBAL_ENVIRONMENT) globalEnv: RawEnv,
    @Optional() private readonly transferState: TransferState,
    private readonly winRef: WindowRefService<EnvWindow>,
    private readonly platform: PlatformService,
    private readonly cookie: CookieService
  ) {
    this.setupEnv(staticEnv, globalEnv);
  }

  get pathToAssets(): string {
    return this.localEnv.resourcePath || '';
  }

  // TODO: In Hybris context this property first has to be provided
  get logLevel(): LogLevel | keyof typeof LogLevel {
    return this.localEnv.server?.logLevel || this.localEnv.logLevel || LogLevel.ERROR;
  }

  get country(): string {
    return this.localEnv.country || 'US';
  }

  get hub(): string {
    return this.localEnv.hub || this.localEnv.region;
  }

  get language(): string {
    return this.localEnv.language || 'en';
  }

  get availableLanguages(): CountryLanguage[] {
    return this.localEnv.languages || [];
  }

  /**
   * Standard "Java" locale (`en_US` or `de_DE`)
   */
  get locale(): string {
    return this.localEnv.locale || 'en_US';
  }

  /**
   * Didomi is a third a party used for consent management, it require a language.
   * for some of the languages that we have are different than what Didomi is expecting.
   */
  get didomiLanguage(): string {
    return this.localEnv.didomiLanguage;
  }

  get isByDesign(): boolean {
    return !!this.localEnv.isByDesign;
  }

  /**
   * BCP 47 language-tag, which is different from standard locale:
   * * using hyphen as separator (`en-US`)
   * * if lowercase language and country is equal it only uses one of them (`de` instead of `de-DE`)
   */
  get languageTag(): string {
    return this.localEnv.languageTag;
  }

  /**
   * Long version of BCP 47 language-tag, which always consists of language and country
   */
  get languageTagLong(): string {
    return this.localEnv.languageTagLong;
  }

  get mobileApp(): boolean {
    return !!this.localEnv.mobileAppParams || !!this.localEnv.mobileApp;
  }

  get frontendMode(): FrontendMode {
    return this.localEnv.frontendMode;
  }

  get isMixedMode(): boolean {
    return this.frontendMode === FrontendMode.MIXED;
  }

  /**
   * Indicates if app runs in AEM/Hybris host context,
   * which means, app will render at least header & footer
   */
  get hostContext(): HostContext {
    return this.localEnv.hostContext;
  }

  /**
   * Indicates if app runs in AEM/Hybris host context without any Angular content
   */
  get pageFrameOnly(): boolean {
    return !!this.hostContext && ![FrontendMode.FULL, FrontendMode.MIXED].includes(this.localEnv.frontendMode);
  }

  /**
   * getter is more or less for transferring compressesLayout state
   * from server to frontend, so it will be initially available,
   * even before route data containing the setting has been resolved.
   */
  get compressedPageLayout(): boolean {
    return this.localEnv.compressedLayout;
  }

  set compressedPageLayout(compressed: boolean) {
    this.localEnv.compressedLayout = compressed;
  }

  get mobileAppParams(): MobileAppParameters {
    return this.localEnv.mobileAppParams || ({} as MobileAppParameters);
  }

  get isProduction(): boolean {
    return this.localEnv.production || false;
  }

  get isStandalone(): boolean {
    return this.localEnv.standalone || false;
  }

  get devBuild(): DevBuild {
    return this.localEnv.devBuild;
  }

  get ssrCachingEnabled(): boolean {
    return this.isStandalone && this.platform.isServer;
  }

  get gqlEndpointUri(): string {
    return this.localEnv.gqlEndpoint || '';
  }

  get cloudBaseUrl(): string {
    return this.localEnv.cloudBaseUrl || '';
  }

  get dusBaseUrl(): string {
    return this.localEnv.dusBaseUrl || '';
  }

  get aemEndpointUrl(): string {
    return this.localEnv.aemEndpointUrl || '';
  }

  get evoucherEndpointUrl(): string {
    return this.localEnv.evoucherEndpointUrl || '';
  }

  get currency(): Currency {
    return this.localEnv.currency || ({} as Currency);
  }

  get currencies(): Currency[] {
    return this.localEnv.currencies || [];
  }

  set currencies(currencies: Currency[]) {
    this.localEnv.currencies = currencies;
  }

  get isRtl(): boolean {
    return !!this.localEnv.rtl;
  }

  get dateFormat(): string {
    return this.localEnv.dateFormat || '';
  }

  get hqTimeZone(): string {
    return this.localEnv.hqTimeZone || '';
  }

  get distanceUnit(): DistanceUnit {
    return this.localEnv.distanceUnit || DistanceUnit.KM;
  }

  get phoneData(): PhoneData {
    return this.localEnv.phone;
  }

  get recaptchaV3PublicKey(): string {
    return this.localEnv.recaptchaV3PublicKey || '';
  }

  get liveChatRecaptchaV3PublicKey(): string {
    return this.localEnv.liveChatRecaptchaV3PublicKey || '';
  }

  get trackingData(): TrackingData {
    return this.localEnv.trackingData || {};
  }

  get categoryData(): CategoryData {
    return this.localEnv.categoryData || ({} as CategoryData);
  }

  get searchData(): object {
    return this.localEnv.searchResults || {};
  }

  get googleMapsClientId(): string {
    return this.localEnv.googleMapsClientId;
  }

  get googleTagManagerId(): string {
    return this.localEnv.server?.googleTagManagerId;
  }

  get didomiApiKey(): string {
    return this.localEnv.server?.didomiApiKey;
  }

  get featureToggles(): Record<string, any> {
    return this.localEnv.server?.featureToggles || this.localEnv.featureToggles;
  }

  get translationPath(): string {
    return this.localEnv.server?.translationPath || this.localEnv.translationPath || '';
  }

  get customLinks(): CustomLinks {
    return this.localEnv.customLinks || ({} as CustomLinks);
  }

  /**
   * This getter is NOT intended for being accessed directly.
   * If you need to get auth token, use `TokenService.authToken` or `TokenService.bearerToken` instead.
   *
   * @deprecated
   */
  get bearerToken(): string {
    return this.localEnv.userBearerToken || '';
  }

  get anonymousToken(): string {
    return this.localEnv.server?.anonymousToken || this.localEnv.anonymousToken || '';
  }

  get oauthClientRedirectHost(): string {
    return this.localEnv.oauthClientRedirectHost;
  }

  get liveChatJsUrl(): string {
    return this.localEnv.liveChatJsUrl || '';
  }

  get liveChatDeploymentId(): string {
    return this.localEnv.liveChatDeploymentId || '';
  }

  /**
   * unique version for the build
   */
  get buildVersion(): string {
    return BUILD_VERSION;
  }

  /**
   * based on build version, but can be named differently
   * additionally, it's being applied from outside.
   * In case it's unset (HOLNG scenario), try to derive it from resourcePath.
   *
   */
  get releaseVersion(): string {
    return (
      this.localEnv.releaseVersion ||
      this.localEnv.resourcePath?.substring(this.localEnv.resourcePath.lastIndexOf('/') + 1) ||
      ''
    );
  }

  /**
   * Used for determining if Angular Universal (standalone) is running on a prod-like environment
   */
  get isProdEnv(): boolean {
    return !!this.localEnv.isProdEnv;
  }

  get salesOrgId(): string {
    return this.localEnv.salesOrgId || '';
  }

  get rapidPayConfig(): RapidPayConfig {
    return this.localEnv.rapidPayConfig || {};
  }

  get cardTokenizationV1EndpointUrl(): string {
    return this.localEnv.cardTokenizationV1EndpointUrl || '';
  }

  get cardTokenizationV2EndpointUrl(): string {
    return this.localEnv.cardTokenizationV2EndpointUrl || '';
  }

  /**
   * Several places can provide env data, which will be merged in this order:
   * * staticEnv from environment.ts
   * * globalEnv injected e.g. in SSR environment
   * * legacyEnv exposed from Hybris via window.__env.hdms in DOM
   */
  private setupEnv(staticEnv: RawEnv, globalEnv: RawEnv): void {
    this.legacyEnv = parseHdmsEnv(this.win.__env?.hdms);
    this.localEnv = { ...this.transferEnv({ ...staticEnv, ...globalEnv }), ...this.legacyEnv };

    this.setLanguageTag();
    this.updateSelectedCurrency();
    this.exposeVersion();
  }

  private setLanguageTag(): void {
    this.localEnv.languageTag ||= generateLanguageTag(this.locale, this.language, this.country);
    this.localEnv.languageTagLong ||= generateLanguageTag(this.locale);
  }

  /**
   * On multi-currency MOs update selected currency based on cookie value (user selection).
   * This is needed in AEM hostContext only, as AEM can't differentiate cached html fragments
   * based on selected currency. Hence, update selected currency after rehydrating the app.
   * @private
   */
  private updateSelectedCurrency(): void {
    const currencies = this.localEnv.currencies;
    const selectedCode =
      currencies &&
      this.hostContext === HostContext.AEM &&
      this.platform.isBrowser &&
      this.cookie.get<string>(DISPLAY_CURRENCY_COOKIE_NAME)?.toUpperCase();

    const selectedCurrency = selectedCode && currencies.find(c => c.code === selectedCode);
    if (selectedCurrency) {
      this.localEnv.currency = selectedCurrency;
    }
  }

  /**
   * Expose the current build and release version to window object
   * so it can be checked via dev tools.
   * @private
   */
  private exposeVersion(): void {
    this.win.hdmsBuildVersion = this.buildVersion;
    this.win.hdmsReleaseVersion = this.releaseVersion;
  }

  /**
   * Add/read raw env to/from TransferState (Universal -> client transition).
   * @param env
   * @private
   */
  private transferEnv(env: RawEnv): RawEnv {
    if (this.transferState) {
      if (this.isBrowser) {
        return { ...env, ...this.transferState.get<RawEnv>(STATE_KEY, {}) };
      } else if (env.standalone) {
        this.setTransferStateOnSerialize(env);
      }
    }
    return env;
  }

  /**
   * Before serializing TransferState, remove server-related and empty props.
   *
   * Also, replace bearerToken property with a placeholder, in order to ensure
   * that cached page html stored in Redis never contains user auth information.
   * (placeholder will be replaced with token before serving the html to the client,
   * no matter if page is instantly rendered by Angular or taken from Redis cache).
   *
   * If app runs in AEM/Hybris host context, take care of app on client side
   * doesn't run with standalone flag and remove all props related to direct GW access,
   * as in such scenario app will use Hybris GraphQL proxy for GraphQL communication.
   * @param env
   * @private
   */
  private setTransferStateOnSerialize(env: RawEnv): void {
    this.transferState.onSerialize(STATE_KEY, () => {
      const cleanedEnv: RawEnv = { ...this.localEnv, server: null, production: null };

      cleanedEnv.userBearerToken = BEARER_TOKEN_PLACEHOLDER;

      if (env.hostContext) {
        delete cleanedEnv.standalone;
        delete cleanedEnv.gqlEndpoint;
        delete cleanedEnv.cloudBaseUrl;
        delete cleanedEnv.dusBaseUrl;
        delete cleanedEnv.evoucherEndpointUrl;
        delete cleanedEnv.anonymousToken;
        delete cleanedEnv.userBearerToken;
      }

      return removeEmptyObjectProps(cleanedEnv);
    });
  }
}
